/* global contractReportsIndexUrl contractReportsDeleteUrl contractReportGenerateUrl channelName */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { format } from 'date-fns';

import Button from 'CommonComponents/Button';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';
import { ToastProvider } from 'Context/ToastContext';

import useApiRequest from 'Hooks/useApiRequest';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';

import DownloadIcon from 'Assets/icons/download.svg';
import HourglassIcon from 'Assets/icons/hourglass.svg';
import InformationCircleIcon from 'Assets/icons/information-circle.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const ArchivedSupplierContractReportsIndex = ({ contractReportsIndexUrl, contractReportsDeleteUrl, contractReportGenerateUrl, channelName }) => {
    const pagination = usePagination({ perPage: 10 });

    const confirm = useConfirmationModal();

    const contractReportsRequest = useApiRequest({
        url: contractReportsIndexUrl,
    });

    const deleteReportRequest = useApiRequest({
        url: contractReportsDeleteUrl,
    });

    const generateReportRequest = useApiRequest({
        url: contractReportGenerateUrl,
    });

    useEffect(() => {
        getReports();
    }, [pagination.page, pagination.perPage]);

    // Subscribe to WebSocket for real-time status updates
    useEffect(() => {
        if (!channelName || !window.Echo) {
            return;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.report.generated', (event) => {
            if (event.report) {
                getReports();
            }
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName]);

    useEffect(() => {
        if (contractReportsRequest.data) {
            pagination.setFrom(contractReportsRequest.data.meta.from);
            pagination.setTo(contractReportsRequest.data.meta.to);
            pagination.setTotal(contractReportsRequest.data.meta.total);
            pagination.setLastPage(contractReportsRequest.data.meta.last_page);
        }
    }, [contractReportsRequest.data]);

    const getReports = () => {
        contractReportsRequest.get({
            page: pagination.page,
            per_page: pagination.perPage,
        });
    };

    const deleteReport = (reportId) => {
        confirm({
            title: $t('Delete Report'),
            message: $t('Are you sure you want to delete this report?'),
            onConfirm: () => {
                deleteReportRequest.delete(null, { CONTRACT_REPORT_ID: reportId }).then(() => {
                    if (pagination.from === pagination.to && pagination.page > 1) {
                        pagination.setPage(pagination.page - 1);
                    } else {
                        getReports();
                    }
                });
            },
        });
    };

    const handleGenerateReport = () => {
        generateReportRequest.post().then(() => {
            if (pagination.page === 1) {
                getReports();
            } else {
                pagination.setPage(1);
            }
        });
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                <div className="tw-flex tw-flex-col tw-gap-y-1">
                    <h3 className="table__header">{$t('Archived Reports')}</h3>
                    <div className="table__pagination-info">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => pagination.setPage(+data.selected + 1)}
                            currentPage={pagination.page}
                        />
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <PerPageSelect
                        value={pagination.perPage}
                        onChange={(e) => {
                            pagination.setPerPage(parseInt(e.target.value));
                        }}
                    />
                    <Button className="disabled:tw-bg-blue-25" variant="primary" onClick={handleGenerateReport}>
                        {$t('Generate Report')}
                    </Button>
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table tw-table-fixed">
                    <thead>
                        <tr>
                            <th className="tw-w-40">{$t('Date')}</th>
                            <th className="tw-w-28">{$t('Status')}</th>
                            <th>{$t('Name')}</th>
                            <th className="tw-w-32">{$t('Budget')}</th>
                            <th className="tw-w-24">{$t('Format')}</th>
                            <th className="tw-w-28"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {contractReportsRequest.loading && <TableRowSkeleton rows={10} columns={6} />}

                        {!contractReportsRequest.loading &&
                            contractReportsRequest.data?.data &&
                            contractReportsRequest.data.data.length > 0 &&
                            contractReportsRequest.data.data.map((report) => (
                                <tr key={report.id}>
                                    <td className="inbud-date-cell">{format(new Date(report.created_at), 'yyyy-MM-dd hh:mm:ss')}</td>
                                    <td>{report.status}</td>
                                    <td>{report.name}</td>
                                    <td>{report.budget_name}</td>
                                    <td>{report.format}</td>
                                    <td>
                                        <div className="tw-flex tw-items-center tw-gap-x-2">
                                            {report.is_complete && (
                                                <a
                                                    href={report.download_url}
                                                    className="text-success text-decoration-none"
                                                    data-popper-toggle="tooltip"
                                                    data-placement="left"
                                                    title="Download"
                                                >
                                                    <DownloadIcon className="tw-w-3.5 tw-h-3.5" />
                                                </a>
                                            )}
                                            {report.is_failed && (
                                                <InformationCircleIcon
                                                    className="tw-w-3.5 tw-h-3.5 tw-text-red-500"
                                                    data-popper-toggle="tooltip"
                                                    data-placement="left"
                                                    title="Failed"
                                                />
                                            )}
                                            {report.is_in_progress && (
                                                <HourglassIcon
                                                    className="tw-w-3.5 tw-h-3.5 tw-text-yellow-500"
                                                    data-popper-toggle="tooltip"
                                                    data-placement="left"
                                                    title="In Progress"
                                                />
                                            )}
                                            {!report.is_in_progress && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => deleteReport(report.id)}
                                                    data-popper-toggle="tooltip"
                                                    data-placement="left"
                                                    title="Delete"
                                                >
                                                    <TrashIcon className="tw-w-3 tw-h-3 tw-text-red" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        {!contractReportsRequest.loading && contractReportsRequest.data?.total === 0 && (
                            <tr>
                                <td colSpan="7">{$t('No Report found')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

if (document.getElementById('ArchivedSupplierContractReportsIndex')) {
    var rootEl = document.getElementById('ArchivedSupplierContractReportsIndex');
    ReactDOM.render(
        <App>
            <ToastProvider>
                <ConfirmationModalProvider>
                    <ArchivedSupplierContractReportsIndex
                        contractReportsIndexUrl={contractReportsIndexUrl}
                        contractReportsDeleteUrl={contractReportsDeleteUrl}
                        contractReportGenerateUrl={contractReportGenerateUrl}
                        channelName={channelName}
                    />
                </ConfirmationModalProvider>
            </ToastProvider>
        </App>,
        rootEl,
    );
}
